import api from './api';
import { SMAART_ROLE_LIBRARY, findRoleByExactTitle } from '../data/roleSkillsLibrary';

/**
 * Role → skills, sourced ALWAYS from the curated local library
 * (roleSkillsLibrary.js — 1,001 roles, 55 job families).
 *
 * This is the single source of truth for role auto-fill.
 * The database endpoint is only used for role-title search suggestions
 * when the static library has no match.
 */

/** Same level/essential mapping used throughout the skill pickers. */
const CATEGORY_SHAPE = {
  technical:  { requiredLevel: 3, isEssential: true },
  domain:     { requiredLevel: 2, isEssential: true },
  'ai-tool':  { requiredLevel: 2, isEssential: false }
};

/** Convert a DB role doc to structured skills (used only as a deep fallback). */
export const dbRoleToStructuredSkills = (dbRole) =>
  (dbRole?.skills || []).map((s) => {
    const shape = CATEGORY_SHAPE[String(s.category || '').toLowerCase()] || CATEGORY_SHAPE.technical;
    return { skill_name: s.skill_name, ...shape, category: s.category || 'Technical' };
  });

/**
 * Search role-title suggestions — always from the local library first,
 * then the DB if we got fewer than 5 local hits.
 */
export const searchRoles = async (query) => {
  const q = String(query || '').trim().toLowerCase();
  if (q.length < 2) return [];

  // Local library search (instant, no network)
  const localMatches = SMAART_ROLE_LIBRARY
    .filter(r => r.roleName.toLowerCase().includes(q))
    .slice(0, 10)
    .map(r => ({ roleName: r.roleName, jobFamily: r.jobFamily }));

  if (localMatches.length >= 5) return localMatches;

  // Top up from DB if local library returned fewer than 5
  try {
    const { data } = await api.get('/skillslist/roles', { params: { search: query.trim() } });
    const dbMatches = data?.success ? (data.data || []) : [];
    // Merge, deduplicate by roleName
    const seen = new Set(localMatches.map(r => r.roleName.toLowerCase()));
    const merged = [...localMatches];
    for (const r of dbMatches) {
      if (!seen.has(r.roleName.toLowerCase())) {
        seen.add(r.roleName.toLowerCase());
        merged.push(r);
      }
      if (merged.length >= 10) break;
    }
    return merged;
  } catch {
    return localMatches;
  }
};

/** Every role with its skill names — feeds the bulk-upload Excel template. */
export const fetchAllRolesWithSkills = async () => {
  // Primary: local library (always available, no network needed)
  if (SMAART_ROLE_LIBRARY.length > 0) {
    return SMAART_ROLE_LIBRARY.map(r => ({
      roleName: r.roleName,
      jobFamily: r.jobFamily,
      skills: [
        ...(r.technicalSkills || []).map(s => ({ skill_name: s, category: 'Technical' })),
        ...(r.domainSkills || []).map(s => ({ skill_name: s, category: 'Domain' })),
        ...(r.aiToolSkills || []).map(s => ({ skill_name: s, category: 'AI-Tool' })),
      ]
    }));
  }
  // Fallback: DB
  const { data } = await api.get('/skillslist/roles', { params: { withSkills: 1 } });
  if (!data?.success) throw new Error('Failed to load role library');
  return data.data || [];
};

const roleSkillCache = new Map();
const normalizeTitleKey = (title) =>
  String(title || '').trim().toLowerCase().replace(/[^a-z0-9]/g, '');

/**
 * Exact-title lookup.
 * PRIMARY: local roleSkillsLibrary.js (instant, curated, 1,001 roles).
 * FALLBACK: DB API, only if the role is not in the local library.
 */
export const fetchRoleSkillsByTitle = async (title) => {
  const t = String(title || '').trim();
  if (t.length < 3) return null;
  const key = normalizeTitleKey(t);
  if (roleSkillCache.has(key)) return roleSkillCache.get(key);

  // --- Local library lookup (primary) ---
  const localRole = findRoleByExactTitle(t);
  if (localRole) {
    // Convert to the same shape the DB returns so callers work unchanged
    const result = {
      roleName: localRole.roleName,
      jobFamily: localRole.jobFamily,
      skills: [
        ...(localRole.technicalSkills || []).map(s => ({ skill_name: s, category: 'Technical' })),
        ...(localRole.domainSkills || []).map(s => ({ skill_name: s, category: 'Domain' })),
        ...(localRole.aiToolSkills || []).map(s => ({ skill_name: s, category: 'AI-Tool' })),
      ]
    };
    roleSkillCache.set(key, result);
    return result;
  }

  // --- DB fallback (for roles not yet in the static library) ---
  try {
    const { data } = await api.get('/skillslist/role-skills', { params: { title: t } });
    const result = data?.success ? data.data : null;
    roleSkillCache.set(key, result);
    return result;
  } catch {
    return null;
  }
};
